// Moving the selection: ONE session for every gesture that moves or turns it
// (docs/claude/objects.md, *Moving the selection*) — a drag on the plan or in a
// view's plane, Move / Copy / Rotate by base point (on the plan, in a view, in
// 3D), the 3D gizmo's arrows and ring, the arrow keys, Page Up / Down, a typed
// X / Y, Transform ▸ Move / Rotate / Scale, Mirror and the flips, Align, a text
// box's and a floor's own drags. A gesture only works out its step; what every
// move must do is here, once:
//  - begin: what the rooms carry (captured now, so a room slid over a part does
//    not pick it up), the layers held (nothing changes layer under the hand),
//    where each part stood, the copy a Ctrl move or Copy leaves, the undo step;
//  - step: an offset and a rise, or a turn, from where they started — through
//    the drawingObjects adapters, the carried contents first, so a room re-carves
//    round where they stand; or one object's own offset (an arrange), with what
//    it carries; or a map or a mirror of them all (Scale, Mirror) — or a map of
//    one (a section view's Scale) —, what they carry the same way;
//  - end: kept — the copy left where they stood, what moved re-anchored and its
//    rooms re-carved (a part mounted by where it is dropped mounted again after
//    a step in plan), the hold let go (a part now out of reach keeps the layer it
//    came from), the dimensions following, one undo step; cancelled — the
//    drawing back where it began, nothing recorded; dropped — let go (the
//    drawing was replaced under it).
// DOM-free: main.ts injects the app as a `MoveHost` (the unit tests, a fake one).
import type {
  ProductInstance,
  Vec2,
} from '../shared/types';
import {
  mirrorItems,
  movableOf,
  type ObjectAdapters,
  type ObjItem,
  type PointMap,
  transformItems,
} from './drawingObjects';
import type { StepEnd } from './undoHistory';

/** What a session needs from the app. `C`: the copy it leaves; `M`: an undo mark. */
export interface MoveHost<C, M> {
  readonly adapters: ObjectAdapters;
  /** What stands in these rooms and is not among them (none on a locked layer). */
  carryOf(insts: readonly ProductInstance[]): ProductInstance[];
  /** Hold the nest these parts have now, until `release`. */
  hold(parts: readonly string[]): void;
  /** Let the hold go: kept, a part now standing out of reach keeps its layer. */
  release(keep: boolean): void;
  /** After a kept move of these parts (`from`: where each stood): re-anchor
   *  them, re-carve the rooms, and — `mount` — mount a part mounted by where it
   *  is dropped again where it landed. `anchors`: what the rooms carried, to
   *  re-anchor too — after a mirror or a scale its place in its room's frame
   *  changed. */
  remount(parts: readonly string[], from: ReadonlyMap<string, Vec2>, mount: boolean, anchors: readonly string[]): void;
  syncDimensions(): void;
  /** The objects as they stand, to leave a copy of; null: nothing to copy. */
  takeCopy(): C | null;
  /** Leave the copy where the objects stood; the moved ones stay selected. */
  leaveCopy(copy: C): void;
  /** The one undo history (undoHistory.ts). */
  readonly steps: { begin(): M; mark(): M; push(from?: M): void; end(how: StepEnd, from: M): void };
}

/** How the undo step begins: `open` a gesture (a drag, a handle's press);
 *  `mark` the drawing without opening one (a base pick — its click's mouseup
 *  commits any open gesture); `push` a step now, as `pushHistory` does (a key, a
 *  typed figure: one step that is never put back). */
export type MoveStep = 'open' | 'mark' | 'push';

export interface MoveOptions<M = unknown> {
  step: MoveStep;
  /** Take the copy now (Ctrl at the press, the Copy tool). */
  copy?: boolean;
  /** `push` only: where the step began, when the drawing changed before the
   *  move did (Mirror places its copies, then mirrors them: one step). */
  from?: M;
}

/** One object as a session moves it (drawingObjects.ts `movableOf`). */
export type MovableItem = ReturnType<typeof movableOf>;

export interface MoveSession<M> {
  /** Where the move's undo step began. */
  readonly start: M;
  /** The objects moved, as the adapters move them. */
  readonly movables: readonly MovableItem[];
  /** What their rooms carry along. */
  readonly carry: readonly ProductInstance[];
  /** Every id that moves: the objects and what they carry. */
  readonly moving: ReadonlySet<string>;
  /** What has been applied since the start: the offset (mm), the rise, the turn (degrees CCW). */
  readonly applied: Readonly<{ x: number; y: number; z: number; deg: number }>;
  /** Whether a copy is taken (it is left when the move is kept). */
  readonly copying: boolean;
  readonly live: boolean;
  /** Put them `d` from where they started (`z`: the rise; absent, as it is); false when nothing changed. */
  moveTo(d: { x: number; y: number; z?: number }): boolean;
  /** Turn them `deg` (CCW) about `about` from where they started; false when nothing changed. */
  turnTo(deg: number, about: Vec2): boolean;
  /** Move one of them, with what it carries, `d` further (`z`: a rise) — an
   *  arrange moves each its own way; false when nothing changed. */
  moveOne(id: string, d: { x: number; y: number; z?: number }): boolean;
  /** Map them, and what they carry the same way, through `m` (a scale) — one
   *  step, not from the start. `id`: only that one of them, with what ITS room
   *  carries (a section view's Scale maps each about its own point). A map that
   *  scales heights (`foot`) sets them on purpose, as a rise does. */
  transform(m: PointMap, id?: string): void;
  /** Mirror them, and what they carry the same way, across the line a–b. */
  mirror(a: Vec2, b: Vec2): void;
  /** A step the gesture writes itself (a part seated on a wall as it is
   *  dragged; a tilt in a section view: `rise`, it sets heights on purpose). */
  own(write: () => void, rise?: boolean): void;
  /** Take the copy now, if none is taken (a drag's first frame with Ctrl). */
  takeCopy(): void;
  /** Whether anything moved since the start. */
  moved(): boolean;
  /** Close it (see the header); `copy: false` leaves no copy (Ctrl let go). */
  end(how: StepEnd, opts?: { copy?: boolean }): void;
}

type Placed = { x: number; y: number; rotation: number };

/** Begin moving `items`: see the header. */
export function beginMove<C, M>(host: MoveHost<C, M>, items: readonly ObjItem[], opts: MoveOptions<M>): MoveSession<M> {
  const movables = items.map((it) => movableOf(host.adapters, it));
  const parts = items.flatMap((it) => (it.kind === 'instance' ? [it.obj] : []));
  // What each room carries, each part once: none of the objects moved, nor what
  // another room already takes.
  const moving = new Set(movables.map((m) => m.id));
  const carryBy = new Map<string, ProductInstance[]>();
  for (const p of parts) {
    const own = host.carryOf([p]).filter((c) => !moving.has(c.id));
    for (const c of own) moving.add(c.id);
    if (own.length) carryBy.set(p.id, own);
  }
  const carry = [...carryBy.values()].flat();
  const from = new Map<string, Placed>(parts.map((p) => [p.id, { x: p.x, y: p.y, rotation: p.rotation }]));
  // Where every other object is placed by (a text box's anchor, a floor's first corner).
  const others = movables.filter((m) => !from.has(m.id)).map((m) => ({ m, at: m.origin() }));
  let copy = opts.copy ? host.takeCopy() : null;
  if (opts.step === 'push') host.steps.push(opts.from);
  const start = opts.step === 'open' ? host.steps.begin() : host.steps.mark();
  host.hold(parts.map((p) => p.id));
  const applied = { x: 0, y: 0, z: 0, deg: 0 };
  let rose = false;
  let stepped = false;
  /** A mirror or a scale: what the rooms carry no longer stands where its anchor says. */
  let reshaped = false;
  let live = true;
  const A = host.adapters;
  const carryStep = (cs: readonly ProductInstance[], dx: number, dy: number, dz: number): void => {
    for (const c of cs) {
      if (dx || dy) A.instance.move(c, dx, dy);
      if (dz) A.instance.raise?.(c, dz);
    }
  };
  const carried = (cs: readonly ProductInstance[]): ObjItem[] => cs.map((o): ObjItem => ({ kind: 'instance', obj: o }));
  /** The objects and what they carry (a room settles after its contents moved). */
  const all = (): ObjItem[] => [...items, ...carried(carry)];
  const placedAway = (): boolean => parts.some((p) => {
    const f = from.get(p.id)!;
    return p.x !== f.x || p.y !== f.y || p.rotation !== f.rotation;
  }) || others.some(({ m, at }) => {
    const o = m.origin();
    return o.x !== at.x || o.y !== at.y;
  });
  const session: MoveSession<M> = {
    start, movables, carry, moving, applied,
    get copying() { return !!copy; },
    get live() { return live; },
    moveTo(d) {
      const dx = d.x - applied.x, dy = d.y - applied.y, dz = d.z == null ? 0 : d.z - applied.z;
      if (!dx && !dy && !dz) return false;
      // The carried contents first: a moved room re-carves round where they stand.
      carryStep(carry, dx, dy, dz);
      for (const m of movables) {
        if (dx || dy) m.move(dx, dy);
        m.raise(dz);
      }
      applied.x = d.x;
      applied.y = d.y;
      if (d.z != null) applied.z = d.z;
      return true;
    },
    turnTo(deg, about) {
      const d = deg - applied.deg;
      if (!d) return false;
      const rad = (d * Math.PI) / 180, c = Math.cos(rad), s = Math.sin(rad);
      const fn = (p: Vec2): Vec2 => {
        const dx = p.x - about.x, dy = p.y - about.y;
        return { x: about.x + dx * c - dy * s, y: about.y + dx * s + dy * c };
      };
      transformItems(A, all(), { fn, rot: d, scale: 1 });
      applied.deg = deg;
      return true;
    },
    moveOne(id, d) {
      const m = movables.find((x) => x.id === id), dz = d.z ?? 0;
      if (!m || (!d.x && !d.y && !dz)) return false;
      carryStep(carryBy.get(id) ?? [], d.x, d.y, dz);
      if (d.x || d.y) m.move(d.x, d.y);
      m.raise(dz);
      stepped = true;
      if (dz) rose = true;
      return true;
    },
    transform(m, id) {
      const at = id == null ? -1 : movables.findIndex((x) => x.id === id);
      if (id != null && at < 0) return;
      transformItems(A, at < 0 ? all() : [items[at], ...carried(carryBy.get(id!) ?? [])], m);
      stepped = reshaped = true;
      if (m.foot != null) rose = true;
    },
    mirror(a, b) {
      mirrorItems(A, all(), a, b);
      stepped = reshaped = true;
    },
    own(write, rise = false) {
      write();
      if (rise) rose = true;
    },
    takeCopy() {
      if (!copy) copy = host.takeCopy();
    },
    moved: () => !!(applied.x || applied.y || applied.z || applied.deg || rose || stepped) || placedAway(),
    end(how, o = {}) {
      if (!live) return;
      live = false;
      if (how !== 'keep') {
        if (opts.step !== 'push') host.steps.end(how, start);
        host.release(false);
        return;
      }
      const moved = session.moved();
      if (copy && moved && o.copy !== false) host.leaveCopy(copy);
      // Mounted again only after a step in plan: a rise or a tilt set its height.
      if (moved) host.remount(parts.map((p) => p.id), from, !applied.z && !rose, reshaped ? carry.map((c) => c.id) : []);
      host.release(true);
      host.syncDimensions();
      if (opts.step !== 'push') host.steps.end('keep', start);
    },
  };
  return session;
}
